import { makeObservable, observable, action, computed, runInAction } from 'mobx';
import { getFirebase } from '@assets/firebase/FirebaseAdapater';
import { ref, get } from 'firebase/database';
import { FieldsSchema, SchemaFieldKeys } from './FieldsSchema';

// Types for backtesting data
export interface BacktestResult {
    test_id: string;
    source_of_truth_deal_id: string;
    test_run_deal_id: string;
    results: {
        [fieldName: string]: {
            is_match: boolean;
            confidence: number;
            error_type?: string;
            source_of_truth_value: any;
            test_run_value: any;
            field_name: string;
        };
    };
    metadata: {
        overall_accuracy: number;
        source_of_truth_fields_count: number;
        total_fields: number;
        timestamp: string;
        backend_version: string;
        run_at: string;
        executed_by: string;
    };
}

export interface VersionData {
    version: string;
    deals: string[];
    fields: string[];
    tests: BacktestResult[];
    fieldAccuracies: {
        [field: string]: {
            accuracy: number;
            total: number;
            matches: number;
            dealResults: {
                [dealId: string]: {
                    is_match: boolean;
                    source_of_truth_value: any;
                    test_run_value: any;
                    timestamp: string;
                }
            }
        }
    };
    overallAccuracy: number;
    metadata: {
        timestamp: string;
        run_at: string;
        executed_by: string;
        backend_version: string;
    };
}

export interface VersionComparison {
    field: string;
    displayName: string;
    versionResults: {
        [version: string]: {
            accuracy: number;
            total: number;
            matches: number;
        } | null; // null indicates field wasn't tested in this version
    };
}

export class BacktestingStore {
    @observable accessor backtestingData: { [dealId: string]: { [testId: string]: BacktestResult } } | null = null;
    @observable accessor loading: boolean = true;
    @observable accessor error: string | null = null;

    constructor() {
        makeObservable(this);
    }

    private getFieldDisplayName(fieldName: string): string {
        // Try to get display name from FieldsSchema
        if (fieldName in FieldsSchema) {
            const schema = FieldsSchema[fieldName as SchemaFieldKeys];
            // Check if schema has a display property in any of its properties
            if ('yearly' in schema && schema.yearly) {
                return fieldName.replace(/_per_year$/, '').split('_').map(word => 
                    word.charAt(0).toUpperCase() + word.slice(1)
                ).join(' ');
            }
        }

        // Handle special cases
        const specialCases: { [key: string]: string } = {
            'top_1_customers_revenue_percentage': 'Top 1 Customers Revenue %',
            'top_10_customers_revenue_percentage': 'Top 10 Customers Revenue %',
            'company_name': 'Company Name',
            'company_ceo': 'Company CEO',
            'hq_location': 'HQ Location',
            'year_founded': 'Year Founded',
            'capitalized_software_override': 'Capitalized Development Costs',
            'maintenance_capex_per_year': 'Maintenance CapEx',
            'net_revenue_per_year': 'Revenue',
            'gross_profit_per_year': 'Gross Profit',
            'ebitda_per_year': 'EBITDA',
            'adjusted_ebitda_per_year': 'Adj. EBITDA',
            'gross_retention_lost_only_per_year': 'Gross Retention (Lost Only)',
            'net_retention_yearly': 'Net Retention',
            'total_current_assets_yearly': 'Current Assets',
            'total_current_liabilities_yearly': 'Current Liabilities',
            'capex_per_year': 'Capex'
        };

        return specialCases[fieldName] || fieldName.split('_').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
    }

    @action
    async fetchData() {
        try {
            this.loading = true;
            this.error = null;
            
            const firebase = getFirebase();
            const backTestRef = ref(firebase.db, 'backtest_results');
            const data = await get(backTestRef);
            
            if (data.exists()) {
                runInAction(() => {
                    this.backtestingData = data.val();
                });
            } else {
                runInAction(() => {
                    this.backtestingData = {};
                });
            }
        } catch (err) {
            console.error('Error fetching backtesting data:', err);
            runInAction(() => {
                this.error = 'Failed to load backtesting data';
            });
        } finally {
            runInAction(() => {
                this.loading = false;
            });
        }
    }

    @computed 
    get processedData() {
        if (!this.backtestingData) return null;
        
        // Collect all tests
        const allTests: BacktestResult[] = [];
        Object.entries(this.backtestingData).forEach(([dealId, dealTests]) => {
            Object.values(dealTests).forEach(test => {
                allTests.push(test);
            });
        });
        
        // Group tests by version
        const versionMap = new Map<string, BacktestResult[]>();
        allTests.forEach(test => {
            const version = test.metadata.backend_version || 'unknown';
            if (!versionMap.has(version)) {
                versionMap.set(version, []);
            }
            versionMap.get(version)!.push(test);
        });
            
        // Process each version
        const versions: VersionData[] = Array.from(versionMap.entries()).map(([version, tests]) => {
            // Get unique deals and fields for this version
            const deals = Array.from(new Set(tests.map(t => t.source_of_truth_deal_id)));
            const fields = Array.from(new Set(
                tests.flatMap(t => Object.keys(t.results))
            ));
            
            // Calculate field accuracies
            const fieldAccuracies: VersionData['fieldAccuracies'] = {};
            fields.forEach(field => {
                const fieldResults = tests.filter(t => field in t.results);
                const matches = fieldResults.filter(t => t.results[field].is_match).length;
        
                // Collect results per deal
                const dealResults: { [dealId: string]: any } = {};
                fieldResults.forEach(test => {
                    dealResults[test.source_of_truth_deal_id] = {
                        is_match: test.results[field].is_match,
                        source_of_truth_value: test.results[field].source_of_truth_value,
                        test_run_value: test.results[field].test_run_value,
                        timestamp: test.metadata.timestamp
                    };
                });
        
                fieldAccuracies[field] = {
                    accuracy: matches / fieldResults.length,
                    total: fieldResults.length,
                    matches,
                    dealResults
                };
            });

            // Calculate overall accuracy
            const overallAccuracy = tests.reduce((sum, test) => sum + test.metadata.overall_accuracy, 0) / tests.length;

            // Get the most recent test timestamp for this version
            const latestTest = tests.reduce((latest, test) => {
                const testTime = new Date(test.metadata.timestamp).getTime();
                return !latest || testTime > new Date(latest.metadata.timestamp).getTime() ? test : latest;
            }, tests[0]);

            return {
                version,
                deals,
                fields,
                tests,
                fieldAccuracies,
                overallAccuracy,
                metadata: {
                    timestamp: latestTest.metadata.timestamp,
                    run_at: latestTest.metadata.run_at,
                    executed_by: latestTest.metadata.executed_by,
                    backend_version: latestTest.metadata.backend_version
                }
            };
        });

        // Sort versions by timestamp
        versions.sort((a, b) => {
            const aTime = new Date(a.metadata.timestamp).getTime();
            const bTime = new Date(b.metadata.timestamp).getTime();
            return aTime - bTime;
        });

        // Get all unique fields that have ever been tested
        const allFields = Array.from(new Set(
            versions.flatMap(v => v.fields)
        )).sort();

        // Create version comparison data
        const fieldComparisons: VersionComparison[] = allFields.map(field => {
            const displayName = this.getFieldDisplayName(field);
            const versionResults: { [version: string]: any } = {};

            versions.forEach(v => {
                if (field in v.fieldAccuracies) {
                    const accuracy = v.fieldAccuracies[field];
                    versionResults[v.version] = {
                        accuracy: accuracy.accuracy,
                        total: accuracy.total,
                        matches: accuracy.matches
                    };
                } else {
                    versionResults[v.version] = null;
                }
            });

            return {
                field,
                displayName,
                versionResults
            };
        });

        // Sort fields alphabetically by display name
        fieldComparisons.sort((a, b) => a.displayName.localeCompare(b.displayName));
        
        return {
            versions,
            fieldComparisons,
            allFields
        };
    }
}

// Create a singleton instance
const backtestingStore = new BacktestingStore();

// Hook to use the store
export const useBacktestingStore = () => backtestingStore; 